/**
 * Integration feeds — which gateways publish to this estate, and what each one describes.
 *
 * A "source" is one publishing feed, identified on the wire by (MQTT topic, siteName).
 * MANY SOURCES MAY SERVE ONE SITE, which is the point: an RS-485 gateway polls tens of
 * registers a second, so a data hall of several hundred devices needs several of them.
 * Before this existed the platform resolved a tenant from `siteName` alone, so a site had
 * exactly one feed by construction.
 *
 * `claimBasis` is the field worth understanding. A discovered feed has no owner — the
 * envelope carries no tenant, no serial, no credential — so the server offers a pending
 * feed only on evidence it already holds, and says what that evidence was. See the
 * backend's `modules/integrations/service.ts` header; the UI's job is to show the reason
 * rather than a bare row.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pollInterval } from './backoff';
import { api } from './client';

/** Why a pending feed is being offered to this organisation. Null once assigned. */
export type ClaimBasis = 'your-topic' | 'unclaimed-topic';

export type SourceStatus = 'pending' | 'assigned' | 'ignored';

export interface IngestSource {
  id: string;
  /** `null` means "any subscribed topic" — see `wildcard`. */
  topic: string | null;
  sourceKey: string;
  siteId: string | null;
  siteName: string | null;
  label: string | null;
  vendor: string;
  formatVersion: string;
  status: SourceStatus;
  statusReason: string | null;
  assignedAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  payloadCount: number;
  sampleDevices: string[] | null;
  deviceCount: number;
  /** Device rows this feed has actually created. Zero for anything never assigned. */
  storedDevices: number;
  claimBasis: ClaimBasis | null;
  /**
   * The feed matches on name alone, across every subscribed topic.
   *
   * Only migration 0022's backfilled rows are like this, and only because no table
   * recorded which topic those payloads arrived on — inventing one would have been
   * fabricating a wire fact. It is a standing risk worth naming in the UI: if the broker
   * ever carries another integration's site under the same name on a different topic, a
   * wildcard feed would admit it.
   */
  wildcard: boolean;
}

export interface SourceList {
  sources: IngestSource[];
  counts: Record<SourceStatus, number>;
  truncated: boolean;
}

const KEY = ['integration-sources'] as const;

export function useSources(params?: { status?: SourceStatus; siteId?: string; q?: string }) {
  return useQuery({
    queryKey: [...KEY, params?.status ?? null, params?.siteId ?? null, params?.q ?? null],
    queryFn: () =>
      api.get<SourceList>('/integrations/sources', {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.siteId ? { siteId: params.siteId } : {}),
        ...(params?.q ? { q: params.q } : {}),
      }),
    /*
     * A pending feed publishes every 180s and the operator is watching for it to appear.
     * Slower than that and the queue looks empty while a gateway is talking; faster and
     * it is polling for nothing.
     */
    refetchInterval: pollInterval(60_000),
  });
}

export function useSiteSources(siteId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'site', siteId],
    queryFn: () =>
      api.get<{ sources: IngestSource[]; wildcards: number }>(
        `/integrations/sites/${siteId}/sources`,
      ),
    enabled: Boolean(siteId),
  });
}

export function useSourceDevices(sourceId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'devices', sourceId],
    queryFn: () =>
      api.get<{
        devices: Array<{ id: number; deviceName: string; status: string; label: string | null }>;
      }>(`/integrations/sources/${sourceId}/devices`),
    enabled: Boolean(sourceId),
  });
}

/**
 * Everything an assignment invalidates. Listed once so no call site forgets one.
 *
 * `devices` and `device-counts` are in here because assigning a feed is what makes its
 * devices start appearing in the integration queue — the operator's next stop. Leaving
 * them stale means the button says success and the following screen shows nothing.
 */
function invalidateFeedViews(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: KEY });
  void qc.invalidateQueries({ queryKey: ['devices'] });
  void qc.invalidateQueries({ queryKey: ['device-counts'] });
  void qc.invalidateQueries({ queryKey: ['telemetry-health'] });
}

export function useRegisterSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      topic: string;
      sourceKey: string;
      siteId: string;
      label?: string;
      vendor?: string;
      formatVersion?: string;
    }) => api.post<{ source: IngestSource }>('/integrations/sources', input),
    onSuccess: () => invalidateFeedViews(qc),
  });
}

export function useAssignSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; siteId: string; label?: string; topic?: string }) => {
      const { id, ...body } = input;
      return api.post<{ source: IngestSource }>(`/integrations/sources/${id}/assign`, body);
    },
    onSuccess: () => invalidateFeedViews(qc),
  });
}

export function useSourceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; action: 'unassign' | 'ignore'; reason: string }) =>
      api.post<{ source: IngestSource }>(`/integrations/sources/${input.id}/${input.action}`, {
        reason: input.reason,
      }),
    onSuccess: () => invalidateFeedViews(qc),
  });
}

export function usePatchSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      label?: string | null;
      vendor?: string;
      formatVersion?: string;
      topic?: string;
    }) => {
      const { id, ...body } = input;
      return api.patch<{ source: IngestSource }>(`/integrations/sources/${id}`, body);
    },
    onSuccess: () => invalidateFeedViews(qc),
  });
}

export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: true }>(`/integrations/sources/${id}`),
    onSuccess: () => invalidateFeedViews(qc),
  });
}

/* ───────────────────────────────────────────────── splitting a gateway's registers */

export interface DeviceRegister {
  baseKey: string;
  displayName: string | null;
  unit: string | null;
  kind: string;
}

export interface DeviceSplit {
  id: string;
  unitKey: string;
  name: string;
  roleKey: string;
  deviceId: number | null;
}

export interface DeviceRegisters {
  device: { id: number; name: string; siteId: string; keyCount: number };
  /**
   * How many distinct registers the other devices on this site publish, at the median.
   *
   * The evidence, not the verdict. Jamshoro's weather station was found by arithmetic —
   * one inverter publishing 52 keys where its nine siblings each published 42 — and this
   * is that comparison, handed to the screen so it can show the asymmetry rather than
   * making an operator count. What the extra keys MEAN is still a person's call.
   */
  siblingMedianKeyCount: number | null;
  registers: DeviceRegister[];
  splits: DeviceSplit[];
}

export function useDeviceRegisters(deviceId: number | null) {
  return useQuery({
    queryKey: [...KEY, 'registers', deviceId],
    queryFn: () => api.get<DeviceRegisters>(`/integrations/devices/${deviceId}/registers`),
    enabled: deviceId !== null,
  });
}

/**
 * Everything a split OR a combine changes. Wider than `invalidateFeedViews` by the two lists
 * that show a device's parameters: both move series between devices, so a stale telemetry or
 * metrics cache shows the same reading under both machines at once.
 *
 * Shared between the two features rather than duplicated, because the cache footprint of
 * "series moved between devices" is one fact, not two — see `useCreateCombine` below for the
 * many-source case this also covers.
 */
function invalidateDeviceMoveViews(qc: ReturnType<typeof useQueryClient>): void {
  invalidateFeedViews(qc);
  void qc.invalidateQueries({ queryKey: ['telemetry'] });
  void qc.invalidateQueries({ queryKey: ['metrics-config'] });
  void qc.invalidateQueries({ queryKey: ['scene'] });
  /* Each device drawer's parameter list: a moved or combined register leaves one device's list
     and joins another's. */
  void qc.invalidateQueries({ queryKey: ['device-preview'] });
}

export function useCreateSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      deviceId: number;
      unitKey: string;
      name: string;
      roleKey: string;
      vendor?: string;
      model?: string;
      matchKeys: string[];
      matchPrefixes: string[];
    }) => {
      const { deviceId, ...body } = input;
      return api.post<{
        split: { splitId: string; deviceId: number; deviceName: string; seriesMoved: number };
      }>(`/integrations/devices/${deviceId}/splits`, body);
    },
    onSuccess: () => invalidateDeviceMoveViews(qc),
  });
}

export function useRevertSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ reverted: true; seriesReturned: number }>(`/integrations/splits/${id}`),
    onSuccess: () => invalidateDeviceMoveViews(qc),
  });
}

/* ────────────────────────────────────────── combining several devices' registers */

export interface DeviceCombineSource {
  deviceId: number;
  deviceName: string;
  matchKeys: string[];
}

/** Whether a combine's readings are added or averaged (0109). */
export type CombineOp = 'sum' | 'avg';

/** One reading of a combine that adds up, as the server lists it. */
export interface DeviceCombineReading {
  key: string;
  instance: number;
  op: CombineOp;
  unit: string | null;
  name: string;
  /** The derived worker's state for it: active, waiting, error, paused. */
  status: string;
}

export interface DeviceCombine {
  id: string;
  unitKey: string;
  name: string;
  roleKey: string;
  deviceId: number;
  deviceName: string;
  /** `show`: registers shown together. `maths` (0109): readings of its own, its members' added or averaged. */
  mode: 'show' | 'maths';
  /** For a maths combine, its members and the keys each feeds. */
  sources: DeviceCombineSource[];
  readings: DeviceCombineReading[];
  /** A maths combine's history still being worked out: the day reached; null once done. */
  history: { from: string | null; reached: string | null; open: number; total: number } | null;
}

/** A reading every picked device reads, offered for a combine that adds up, with the unit's default. */
export interface CombinableReading {
  key: string;
  instance: number;
  unit: string | null;
  displayName: string | null;
  op: CombineOp;
  picked: boolean;
}

/** What `deviceIds` could be added up or averaged on — fetched once two or more are picked. */
export function useCombinableReadings(deviceIds: readonly number[]) {
  const ids = [...deviceIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: [...KEY, 'combine-readings', ids.join(',')],
    queryFn: () =>
      api.get<{ readings: CombinableReading[] }>('/integrations/combine-readings', { deviceIds: ids.join(',') }),
    enabled: ids.length >= 2,
  });
}

/** A site's existing combines, for the drawer's "already combined" list. */
export function useSiteCombines(siteId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'combines', siteId],
    queryFn: () =>
      api.get<{ combines: DeviceCombine[] }>(`/integrations/sites/${siteId}/combines`),
    enabled: siteId !== null,
  });
}

/**
 * Show registers from one or more devices under one new virtual device. Nothing moves: each
 * reading keeps arriving from its own device, so the combine is live for new readings at once.
 */
export function useCombineDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | {
            unitKey: string;
            name: string;
            roleKey: string;
            vendor?: string;
            model?: string;
            sources: Array<{ deviceId: number; matchKeys: string[] }>;
          }
        | {
            mode: 'maths';
            unitKey: string;
            name: string;
            roleKey: string;
            deviceIds: number[];
            readings: Array<{ key: string; instance: number; op: CombineOp }>;
          },
    ) =>
      api.post<{
        combine: { combineId: string; deviceId: number; deviceName: string; seriesMoved: number };
      }>('/integrations/devices/combine', input),
    onSuccess: () => invalidateDeviceMoveViews(qc),
  });
}

export function useRevertCombine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ reverted: true; seriesReturned: number }>(`/integrations/combines/${id}`),
    onSuccess: () => invalidateDeviceMoveViews(qc),
  });
}
